/**
 * External MIDI playback sink — timed noteon/noteoff (+ export-parity CCs)
 * for slots with `kind: "externalMidi"`. Independent of control-binding MIDI out.
 */

import {
  clampMidi7,
  midiCcFromNoteFx,
  type ExternalMidiPatch,
  type Instrument,
  type InstrumentSlot,
  type VoiceNoteFxParams,
} from "../../../../src/lib/audio";

export type ExternalMidiSinkOptions = {
  getInstrument: (slot: InstrumentSlot) => Instrument | undefined;
  /** AudioContext / engine clock (seconds). */
  getAudioTime: () => number;
  /**
   * Convert AudioContext time → DOMHighResTimeStamp for MIDIOutput.send.
   * Default: performance.now() + (audioTime - getAudioTime()) * 1000.
   */
  audioTimeToDom?: (audioTime: number) => number;
};

type SoundingNote = {
  note: number;
  channel: number;
  outputId: string;
};

function midiStatus(channel: number, statusNibble: number): number {
  const ch = Math.min(16, Math.max(1, Math.round(channel))) - 1;
  return (statusNibble << 4) | ch;
}

export class ExternalMidiSink {
  private access: MIDIAccess | null = null;
  private outputs = new Map<string, MIDIOutput>();
  private sounding = new Map<string, SoundingNote>();
  private ready: Promise<void> | null = null;

  constructor(private readonly options: ExternalMidiSinkOptions) {}

  async ensureAccess(): Promise<boolean> {
    if (this.access) {
      return true;
    }
    if (!navigator.requestMIDIAccess) {
      return false;
    }
    if (!this.ready) {
      this.ready = navigator
        .requestMIDIAccess({ sysex: false })
        .then(access => {
          this.access = access;
          this.refreshOutputs();
          access.onstatechange = () => this.refreshOutputs();
        })
        .catch(() => {
          this.access = null;
        });
    }
    await this.ready;
    return this.access != null;
  }

  listOutputs(): Array<{ id: string; name: string }> {
    this.refreshOutputs();
    return [...this.outputs.values()].map(port => ({
      id: port.id,
      name: port.name ?? port.id,
    }));
  }

  noteOn(
    slot: InstrumentSlot,
    note: number,
    velocity: number,
    audioTime: number,
    noteFx?: VoiceNoteFxParams,
  ): void {
    const patch = this.patchFor(slot);
    if (!patch) {
      return;
    }
    const port = this.resolvePort(patch.outputDeviceId);
    if (!port) {
      return;
    }
    const midiNote = clampMidi7(note);
    const vel = clampMidi7(Math.min(1, Math.max(0, velocity)) * 127);
    const when = this.domTime(audioTime);
    for (const cc of midiCcFromNoteFx(noteFx)) {
      port.send(
        [
          midiStatus(patch.channel, 0xb),
          cc.controller & 0x7f,
          clampMidi7(cc.value01 * 127),
        ],
        when,
      );
    }
    // Retrigger: prior note off first so mono slot is clean.
    this.noteOff(slot, midiNote, audioTime);
    port.send([midiStatus(patch.channel, 0x9), midiNote, Math.max(1, vel)], when);
    this.sounding.set(this.key(slot, midiNote), {
      note: midiNote,
      channel: patch.channel,
      outputId: port.id,
    });
  }

  noteOff(slot: InstrumentSlot, note: number, audioTime: number): void {
    const midiNote = clampMidi7(note);
    const held = this.sounding.get(this.key(slot, midiNote));
    const patch = this.patchFor(slot);
    const outputId = held?.outputId ?? patch?.outputDeviceId ?? "";
    const channel = held?.channel ?? patch?.channel ?? 1;
    const port = this.resolvePort(outputId) ?? (patch ? this.resolvePort(patch.outputDeviceId) : null);
    if (port) {
      port.send(
        [midiStatus(channel, 0x8), midiNote, 0],
        this.domTime(audioTime),
      );
    }
    this.sounding.delete(this.key(slot, midiNote));
  }

  /** Panic all (or one slot's) sounding external MIDI notes. */
  allNotesOff(slot?: InstrumentSlot): void {
    const now = this.domTime(this.options.getAudioTime());
    for (const [key, held] of [...this.sounding]) {
      if (slot !== undefined && !key.startsWith(`${slot}:`)) {
        continue;
      }
      const port = this.resolvePort(held.outputId);
      if (port) {
        port.send([midiStatus(held.channel, 0x8), held.note, 0], now);
        // CC 123 All Notes Off
        port.send([midiStatus(held.channel, 0xb), 123, 0], now);
      }
      this.sounding.delete(key);
    }
  }

  dispose(): void {
    this.allNotesOff();
    this.access = null;
    this.outputs.clear();
    this.ready = null;
  }

  private patchFor(slot: InstrumentSlot): ExternalMidiPatch | null {
    const instrument = this.options.getInstrument(slot);
    if (!instrument || instrument.kind !== "externalMidi") {
      return null;
    }
    return instrument.patch;
  }

  private key(slot: InstrumentSlot, note: number): string {
    return `${slot}:${note}`;
  }

  private resolvePort(deviceId: string): MIDIOutput | null {
    this.refreshOutputs();
    if (!deviceId) {
      return null;
    }
    return this.outputs.get(deviceId) ?? null;
  }

  private refreshOutputs(): void {
    this.outputs.clear();
    if (!this.access) {
      return;
    }
    this.access.outputs.forEach(port => {
      this.outputs.set(port.id, port);
    });
  }

  private domTime(audioTime: number): number {
    if (this.options.audioTimeToDom) {
      return this.options.audioTimeToDom(audioTime);
    }
    const nowAudio = this.options.getAudioTime();
    return performance.now() + (audioTime - nowAudio) * 1000;
  }
}
